const THREE = require('three')
import raf from '../raf'
import {state} from '../main'

export default class ConfettisShader {

  constructor (config) {

    this.state = Object.assign({
      confettisNb: 1000,
      sources: [{x: -1, y: 0, z: 0}, {x: 1, y: 0, z: 0, delay: 0.5}],
      timeStep: (1 / 30),
      time: 5,
      started: false,
      colors: false
    }, config)

    this.mesh = new THREE.Object3D()
    this.mesh.name = `confettis Shader`
    this.mesh.frustumCulled = false

    this.sources = []

    this.initShaders()
    this.initConfettisMaterial()

    // create x sources
    for (var i = 0; i < this.state.sources.length; i++) {
      let source = this.state.sources[i]

      source.material = this.confettisMaterial.clone()
      source.geometry = this.initConfettisGeometry(source.angle)

      source.mesh = new THREE.Mesh(source.geometry, source.material)
      source.mesh.name = `confettis Source ${i}`

      source.mesh.position.set(source.x, source.y, source.z)

      this.sources[i] = source
      this.mesh.add(source.mesh)
    }

    raf.on('tick', () => { this.update() } )
  }

  initShaders() {
    this.vertexShader = require('../shaders/confettis.vs')
    this.fragmentShader = require('../shaders/confettis.fs')
  }

  randomRange(min, max) {
    return Math.random() * (max - min) + min
  }

  initConfettisMaterial() {
    this.shaderAttributes = {
      aAnimation: {type: 'v2', value: null},
      aTranslation: {type: 'v3', value: null},
      aControlPoint0: {type: 'v3', value: null},
      aControlPoint1: {type: 'v3', value: null},
      aAxisAngle: {type: 'v4', value: null},
      aFrontColor: {type: 'c', value: null},
      aBackColor: {type: 'c', value: null}
    }

    this.shaderUniforms = {
      uTime: {type: 'f', value: 0}
    }

    this.confettisMaterial = new THREE.ShaderMaterial({
      // attributes: this.shaderAttributes,
      uniforms: this.shaderUniforms,
      vertexShader: this.vertexShader,
      fragmentShader: this.fragmentShader,
      transparent: true
    })
    this.confettisMaterial.side = THREE.DoubleSide
  }

  initConfettisGeometry(angle) {
    let quads = this.state.confettisNb
    let triangles = quads * 2
    let chunkSize = 21845 // I don't remember why I chose this specific value :(
    let i, j

    let geometry = new THREE.BufferGeometry()
    geometry.setIndex( new THREE.BufferAttribute(new Uint16Array(triangles * 3), 1))
    // geometry.addAttribute('index', new THREE.BufferAttribute(new Uint16Array(triangles * 3), 1))
    geometry.addAttribute('aAnimation', new THREE.BufferAttribute(new Float32Array(triangles * 3 * 2), 2)) // duration, delay
    geometry.addAttribute('position', new THREE.BufferAttribute(new Float32Array(triangles * 3 * 3), 3)) // aStartPosition
    geometry.addAttribute('aTranslation', new THREE.BufferAttribute(new Float32Array(triangles * 3 * 3), 3))
    geometry.addAttribute('aControlPoint0', new THREE.BufferAttribute(new Float32Array(triangles * 3 * 3), 3))
    geometry.addAttribute('aControlPoint1', new THREE.BufferAttribute(new Float32Array(triangles * 3 * 3), 3))
    geometry.addAttribute('aAxisAngle', new THREE.BufferAttribute(new Float32Array(triangles * 3 * 4), 4))
    geometry.addAttribute('aFrontColor', new THREE.BufferAttribute(new Float32Array(triangles * 3 * 3), 3))
    geometry.addAttribute('aBackColor', new THREE.BufferAttribute(new Float32Array(triangles * 3 * 3), 3))

    // index buffer?
    // console.log(geometry)
    let indices = geometry.index.array

    for (i = 0; i < indices.length; i++) {
      indices[i] = i % (3 * chunkSize)
    }

    // buffer animation vars
    let animation = geometry.attributes.aAnimation.array

    for (i = 0; i < animation.length; i += 12) {

      let delay = this.randomRange(0, 0)
      let duration = this.randomRange(3, 20)

      for (j = 0; j < 12; j += 2) {
        animation[i + j + 0] = delay
        animation[i + j + 1] = duration
      }
    }

    // buffer start positions
    let positions = geometry.attributes.position.array
    let halfWidth = 0.02
    let halfHeight = halfWidth * 0.6
    let a = new THREE.Vector3(-halfWidth, halfHeight, 0) // top-left
    let b = new THREE.Vector3(halfWidth, halfHeight, 0) // top-right
    let c = new THREE.Vector3(halfWidth, -halfHeight, 0) // bottom-right
    let d = new THREE.Vector3(-halfWidth, -halfHeight, 0) // bottom-left
    let vertices = [a, d, b, d, c, b]
    let v

    for (i = 0; i < positions.length; i += 18) {
      v = 0

      for (j = 0; j < 18; j += 3) {
        positions[i + j + 0] = vertices[v].x
        positions[i + j + 1] = vertices[v].y
        positions[i + j + 2] = vertices[v].z

        v++
      }
    }

    // buffer translation
    let translations = geometry.attributes.aTranslation.array
    let t = new THREE.Vector3()

    for (i = 0; i < translations.length; i += 18) {
      let phi = Math.random() * Math.PI * 2
      let radius = 4
      let x1 = this.randomRange(-4, 4)
      let y1 = 0
      let z1 = this.randomRange(-4, 4)

      t.x = x1 + radius * Math.cos(phi) * Math.random()
      t.z = z1 + radius * Math.sin(phi) * Math.random()

      for (j = 0; j < 18; j += 3) {
        translations[i + j + 0] = t.x
        translations[i + j + 1] = t.y
        translations[i + j + 2] = t.z
      }
    }

    // buffer control points
    let controlPoints0 = geometry.attributes.aControlPoint0.array
    let controlPoints1 = geometry.attributes.aControlPoint1.array
    let cp0 = new THREE.Vector3()
    let cp1 = new THREE.Vector3()

    for (i = 0; i < controlPoints0.length; i += 18) {
      cp0.x = this.randomRange(angle, 0)
      cp0.y = this.randomRange(10, 0)
      cp0.z = this.randomRange(0, 0)

      cp1.x = this.randomRange(-8, 8)
      cp1.y = this.randomRange(20, 10)
      cp1.z = this.randomRange(-8, 8)

      for (j = 0; j < 18; j += 3) {
        controlPoints0[i + j + 0] = cp0.x
        controlPoints0[i + j + 1] = cp0.y
        controlPoints0[i + j + 2] = cp0.z

        controlPoints1[i + j + 0] = cp1.x
        controlPoints1[i + j + 1] = cp1.y
        controlPoints1[i + j + 2] = cp1.z
      }
    }

    // buffer axis and angle
    let axisAngles = geometry.attributes.aAxisAngle.array
    let a2 = new THREE.Vector3()

    for (i = 0; i < axisAngles.length; i += 24) {
      // axis
      a2.x = Math.random()
      a2.y = 0
      a2.z = Math.random()
      a2.normalize()
      // angle
      a2.w = Math.PI * this.randomRange(0, 60)

      for (j = 0; j < 24; j += 4) {
        axisAngles[i + j + 0] = a2.x
        axisAngles[i + j + 1] = a2.y
        axisAngles[i + j + 2] = a2.z
        axisAngles[i + j + 3] = a2.w
      }
    }

    // buffer colors
    let frontColors = geometry.attributes.aFrontColor.array
    let backColors = geometry.attributes.aBackColor.array

    // function rgbToHsl(r, g, b) {
    //   let r /= 255
    //   let g /= 255
    //   let b /= 255
    //   var max = Math.max(r, g, b), min = Math.min(r, g, b);
    //   var h, s, l = (max + min) / 2;

    //   if(max == min){
    //       h = s = 0; // achromatic
    //   }else{
    //       var d = max - min;
    //       s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    //       switch(max){
    //           case r: h = (g - b) / d + (g < b ? 6 : 0); break;
    //           case g: h = (b - r) / d + 2; break;
    //           case b: h = (r - g) / d + 4; break;
    //       }
    //       h /= 6;
    //   }
    //   return [h, s, l];
    // }


    let hue = 0
    let front = new THREE.Color()
    let back = new THREE.Color()

    for (i = 0; i < frontColors.length; i += 18) {

      if (!this.state.colors) {
        hue = Math.random()
        front.setHSL(hue, 1.0, 0.5)
        back.setHSL(hue, 0.65, 0.5)
      } else {
        // hex to THREE.Color
        let color = this.state.colors[Math.floor(Math.random() * this.state.colors.length)].replace('#','0x')
        front.setHex(color)
        back.setHex(color)
      }

      for (j = 0; j < 18; j += 3) {
        frontColors[i + j + 0] = front.r
        frontColors[i + j + 1] = front.g
        frontColors[i + j + 2] = front.b

        backColors[i + j + 0] = back.r
        backColors[i + j + 1] = back.g
        backColors[i + j + 2] = back.b
      }
    }

    // store offsets
    let offsets = triangles / chunkSize

    for (i = 0; i < offsets; i++) {
      let offset = {
        start: i * chunkSize * 3,
        index: i * chunkSize * 3,
        count: Math.min(triangles - (i * chunkSize), chunkSize) * 3
      }

      geometry.groups.push(offset)
    }

    return geometry
  }

  update() {
    if (!this.state.started) return false

    this.shaderUniforms.uTime.value = this.state.time
    this.shaderUniforms.uTime.needsUpdate = true

    this.state.time += this.state.timeStep

    // if (this.state.time > 0.5)  confettiMaterial2.uniforms.uTime.value += this.state.timeStep
    for (var i = 0; i < this.sources.length; i++) {
      if (this.sources[i].material.uniforms.uTime.value === 0) this.sources[i].material.uniforms.uTime.value = 3
      // if (this.sources[i].delay) {
      if (this.sources[i].delay) {
        if (this.state.time > this.sources[i].delay) this.sources[i].material.uniforms.uTime.value += this.state.timeStep
      } else {
        this.sources[i].material.uniforms.uTime.value += this.state.timeStep
      }
    }
  }

  enable() {
    this.state.started = true
  }

  reset() {
    for (var i = 0; i < this.sources.length; i++) {
      this.sources[i].material.uniforms.uTime.value = 0
    }
    this.state.time = 0
    this.state.started = false
  }
}




// WEBPACK FOOTER //
// ./src/components/game/effects/confettisShader.js